import { useParams } from 'react-router-dom';
import { PromptConfigOutputDtoScopeEnum } from '@kortxio/hub-api';
import {
  aiResponsesParamConfigMap,
  aiResponsesPromptIdParamName,
  aiResponsesScopeParamName,
} from 'features/ai-responses/hooks';
import { CAMPAIGN_TAB, CampaignTab } from 'features/campaign/types';
import { selectedClientSelector } from 'features/client/selectors';
import {
  isReportingOnlyOrFranchiseeRole,
  isReportingOnlyRole,
  roleSelector,
} from 'features/user/selectors';
import { buildPathWithQueryParams } from 'libs/query-param';
import { useAppSelector } from 'store/hooks';
import { StringParam } from 'use-query-params';

export function useIsScopedToClient() {
  const selectedClient = useAppSelector(selectedClientSelector);
  const { clientId } = useParams();

  return selectedClient != null && clientId != null;
}

export function useCanNavigateTo() {
  const role = useAppSelector(roleSelector);
  const isReportingOnlyOrFranchisee = isReportingOnlyOrFranchiseeRole(role);
  const isReportingOnly = isReportingOnlyRole(role);

  return {
    services: !isReportingOnlyOrFranchisee,
    advertisers: !isReportingOnlyOrFranchisee,
    campaigns: !isReportingOnly,
    audiences: !isReportingOnlyOrFranchisee,
    billing: !isReportingOnly,
  };
}

type RouteOverrides = {
  advertiserId?: string | number;
  campaignId?: string | number;
  clientId?: string | number;
  campaignActiveTab?: CampaignTab;
  audienceId?: string | number;
  crmAudienceId?: string | number;
  campaignReportingActiveTab?: string;
  promptConfigId?: string;
};

export const useKampusRoutes = (overrides?: RouteOverrides) => {
  const {
    advertiserId: advertiserIdAsParam,
    campaignId: campaignIdAsParam,
    clientId: clientIdAsParam,
    campaignActiveTab: campaignActiveTabAsParam,
    audienceId: audienceIdAsParam,
    crmAudienceId: crmAudienceIdAsParam,
    activeTab: campaignReportingActiveTabAsParam,
    promptConfigId: promptConfigIdAsParam,
  } = useParams();

  const client = useAppSelector(selectedClientSelector);
  const { id: selectedClientId } = client ?? {};

  const advertiserId = overrides?.advertiserId ?? advertiserIdAsParam;
  const campaignId = overrides?.campaignId ?? campaignIdAsParam;
  const clientId = overrides?.clientId ?? selectedClientId ?? clientIdAsParam;
  const campaignActiveTab =
    overrides?.campaignActiveTab ??
    campaignActiveTabAsParam ??
    CAMPAIGN_TAB.Overview;
  const audienceId = overrides?.audienceId ?? audienceIdAsParam;
  const crmAudienceId = overrides?.crmAudienceId ?? crmAudienceIdAsParam;
  const campaignReportingActiveTab =
    overrides?.campaignReportingActiveTab ?? campaignReportingActiveTabAsParam;
  const promptConfigId = overrides?.promptConfigId ?? promptConfigIdAsParam;

  const role = useAppSelector(roleSelector);

  return {
    routeAdmin: '/admin',
    routeAdminCampaigns: '/admin/campaigns',
    routeAdminCampaignsBulkUpdate: '/admin/campaigns/bulk-update',
    routeAdminUserManagement: '/admin/user-management',
    routeAdminPersonaManagement: '/admin/persona-management',
    routeAdminPromptManagement: '/admin/prompt-management',
    routeAdminPromptManagementById:
      createRouteToPromptManagementById(promptConfigId),
    routeAdminAiResponses: '/admin/ai-responses',
    routeAdminMonitoring: '/admin/monitoring',
    routeAdminMonitoringCommandCenter: '/admin/monitoring/command-center',
    routeAdminMonitoringViolations: '/admin/monitoring/violations',
    routeAdminMonitoringMonitors: '/admin/monitoring/monitors',
    routeAdminMonitoringRunHistory: '/admin/monitoring/run-history',
    routeDemoModeController: '/demo-mode-controller',
    baseClientRoute: createBaseClientRoute(clientId),
    routeHome: createRouteToHome(clientId, role),
    routeServices: createRouteToServices(clientId),
    routeBilling: createRouteToBilling(clientId),
    routeProfile: createRouteToProfile(clientId),
    routeUserManagement: createRouteToUserManagement(clientId),
    routeAdvertisers: createRouteToAdvertisers(clientId),
    routeAdvertiserById: createRouteToAdvertiserById(clientId, advertiserId),
    routeAudiences: createRouteToAudiences(clientId),
    routeAudienceTaxonomy: createRouteToAudienceTaxonomy(
      clientId,
      advertiserId
    ),
    routeAudienceSegmentAnalysis: createRouteToAudienceSegmentAnalysis(
      clientId,
      advertiserId
    ),
    routeAudienceCrm: createRouteToAudienceCrm(clientId, advertiserId),
    routeAudienceCrmDetail: createRouteToAudienceCrmDetail(
      clientId,
      advertiserId,
      crmAudienceId
    ),
    routeAudienceAudienceComposition: createRouteToAudienceComposition(
      clientId,
      advertiserId,
      audienceId
    ),
    routeAudiencePersona: createRouteToAudiencePersona(
      clientId,
      advertiserId,
      audienceId
    ),
    routeCampaigns: createRouteToCampaigns(clientId),
    routeCampaignById: createRouteToCampaignById(
      clientId,
      campaignId,
      campaignActiveTab
    ),
    routeCampaignByIdWithUpdates: createRouteToCampaignById(
      clientId,
      campaignId,
      campaignActiveTab,
      true
    ),
    routeReportingSuite: createRouteToReportingSuite(clientId),
    createRouteToReportingSuiteForAllAdvertisers:
      createRouteToReportingSuiteForAllAdvertisers(clientId),
    routeReportingSuiteByAdvertiserId:
      createRouteToReportingSuiteByAdvertiserId(clientId, advertiserId),
    routeCampaignReportingForAllAdvertisers:
      createRouteToCampaignReportingForAllAdvertisers(clientId),
    routeCampaignReporting: createRouteToCampaignReporting(
      clientId,
      advertiserId
    ),
    routeCampaignReportingByCampaignIdForAllAdvertisers:
      createRouteToCampaignReportingByCampaignIdForAllAdvertisers(
        clientId,
        campaignId
      ),
    routeCampaignReportingByCampaignId:
      createRouteToCampaignReportingByCampaignId(
        clientId,
        advertiserId,
        campaignId
      ),
    routeCampaignReportingTabByCampaignIdForAllAdvertisers:
      createRouteToCampaignReportingTabByCampaignIdForAllAdvertisers(
        clientId,
        campaignId,
        campaignReportingActiveTab
      ),
    routeCampaignReportingTabByCampaignId:
      createRouteToCampaignReportingTabByCampaignId(
        clientId,
        advertiserId,
        campaignId,
        campaignReportingActiveTab
      ),
    routeAIAssistant: createRouteToAIAssistant(
      clientId,
      advertiserId,
      campaignId
    ),
    routeDashboardsForAllAdvertisers:
      createRouteToReportingDashboardsForAllAdvertisers(clientId),
    routeDashboards: createRouteToReportingDashboards(clientId, advertiserId),
    routeReportingSuiteBuilder: createRouteToReportingSuiteBuilder(
      clientId,
      advertiserId
    ),
    routeReportingSuiteBuilderSchedules:
      createRouteToReportingSuiteBuilderSchedules(clientId, advertiserId),
  };
};

export const createRouteToPromptManagementById = (
  promptConfigId?: string | undefined
) => {
  return `/admin/prompt-management/${promptConfigId}`;
};

export const createRouteToAdminMonitoringMonitorConfig = (
  monitorKey: string
) => {
  return `/admin/monitoring/monitors/${monitorKey}/config`;
};

export const createRouteToAdminMonitoringRunHistory = (
  monitorKey?: string,
  runId?: string
) =>
  buildPathWithQueryParams(
    '/admin/monitoring/run-history',
    { monitorKey: StringParam, runId: StringParam },
    { monitorKey, runId }
  );

export const createRouteToAdminAiResponses = (params?: {
  scope?: PromptConfigOutputDtoScopeEnum;
  promptId?: string;
}) =>
  buildPathWithQueryParams('/admin/ai-responses', aiResponsesParamConfigMap, {
    [aiResponsesScopeParamName]: params?.scope,
    [aiResponsesPromptIdParamName]: params?.promptId,
  });

export const createBaseClientRoute = (clientId?: string | number) => {
  return `/clients/${clientId}`;
};

// Not really home anymore since home was removed
export const createRouteToHome = (
  clientId?: string | number,
  role?: string
) => {
  return isReportingOnlyOrFranchiseeRole(role)
    ? `/clients/${clientId}/reporting-suite/all/campaign-reporting`
    : `/clients/${clientId}/campaigns`;
};

export const createRouteToServices = (clientId?: string | number) => {
  return `/clients/${clientId}/services`;
};

export const createRouteToBilling = (clientId?: string | number) => {
  return `/clients/${clientId}/billing`;
};

export const createRouteToProfile = (clientId?: string | number) => {
  return `/clients/${clientId}/profile`;
};

export const createRouteToUserManagement = (clientId?: string | number) => {
  return `/clients/${clientId}/user-management`;
};

export const createRouteToAdvertisers = (clientId?: string | number) => {
  return `/clients/${clientId}/advertisers`;
};

export const createRouteToAdvertiserById = (
  clientId?: string | number,
  advertiserId?: number | string
) => {
  return `/clients/${clientId}/advertisers/${advertiserId}`;
};

export const createRouteToAudiences = (clientId?: number | string) => {
  return `/clients/${clientId}/audiences`;
};

export const createRouteToAudienceSegmentAnalysis = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/segment-analysis`;
};

export const createRouteToAudienceComposition = (
  clientId?: number | string,
  advertiserId?: number | string,
  audienceId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/segment-analysis/${audienceId}/audience-composition`;
};

export const createRouteToAudiencePersona = (
  clientId?: number | string,
  advertiserId?: number | string,
  audienceId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/segment-analysis/${audienceId}/persona`;
};

export const createRouteToAudienceTaxonomy = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/taxonomy`;
};

export const createRouteToAudienceCrm = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/crm`;
};

export const createRouteToAudienceCrmDetail = (
  clientId?: number | string,
  advertiserId?: number | string,
  crmAudienceId?: number | string
) => {
  return `/clients/${clientId}/audiences/${advertiserId}/crm/${crmAudienceId}`;
};

export const createRouteToCampaigns = (clientId?: string | number) => {
  return `/clients/${clientId}/campaigns`;
};

export const createRouteToCampaignById = (
  clientId?: string | number,
  campaignId?: number | string,
  tab?: string,
  openUpdates?: boolean
) => {
  return openUpdates
    ? `/clients/${clientId}/campaigns/${campaignId}/${tab}?openUpdates=${openUpdates}`
    : `/clients/${clientId}/campaigns/${campaignId}/${tab}`;
};

export const createRouteToReportingSuite = (clientId?: number | string) => {
  return `/clients/${clientId}/reporting-suite`;
};

export const createRouteToReportingSuiteForAllAdvertisers = (
  clientId?: number | string
) => {
  return `/clients/${clientId}/reporting-suite/all`;
};

export const createRouteToReportingSuiteByAdvertiserId = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  return `/clients/${clientId}/reporting-suite/${advertiserId}`;
};

export const createRouteToCampaignReportingForAllAdvertisers = (
  clientId?: number | string
) => {
  const reportingSuiteRoute =
    createRouteToReportingSuiteForAllAdvertisers(clientId);

  return `${reportingSuiteRoute}/campaign-reporting`;
};

export const createRouteToCampaignReporting = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  const reportingSuiteRoute = createRouteToReportingSuiteByAdvertiserId(
    clientId,
    advertiserId
  );

  return `${reportingSuiteRoute}/campaign-reporting`;
};

export const createRouteToCampaignReportingByCampaignIdForAllAdvertisers = (
  clientId?: number | string,
  campaignId?: number | string
) => {
  const reportingSuiteRoute =
    createRouteToReportingSuiteForAllAdvertisers(clientId);

  return `${reportingSuiteRoute}/campaign-reporting/${campaignId}`;
};

export const createRouteToCampaignReportingByCampaignId = (
  clientId?: number | string,
  advertiserId?: number | string,
  campaignId?: number | string
) => {
  const reportingSuiteRoute = createRouteToReportingSuiteByAdvertiserId(
    clientId,
    advertiserId
  );

  return `${reportingSuiteRoute}/campaign-reporting/${campaignId}`;
};

export const createRouteToCampaignReportingTabByCampaignIdForAllAdvertisers = (
  clientId?: number | string,
  campaignId?: number | string,
  activeTab?: string
) => {
  const campaignReportingRoute =
    createRouteToCampaignReportingByCampaignIdForAllAdvertisers(
      clientId,
      campaignId
    );

  return `${campaignReportingRoute}/${activeTab}`;
};

export const createRouteToCampaignReportingTabByCampaignId = (
  clientId?: number | string,
  advertiserId?: number | string,
  campaignId?: number | string,
  activeTab?: string
) => {
  const campaignReportingRoute = createRouteToCampaignReportingByCampaignId(
    clientId,
    advertiserId,
    campaignId
  );

  return `${campaignReportingRoute}/${activeTab}`;
};

export const createRouteToAIAssistant = (
  clientId?: string | number,
  advertiserId?: string | number,
  campaignId?: string | number
) => {
  const reportingSuiteRoute = createRouteToReportingSuiteByAdvertiserId(
    clientId,
    advertiserId
  );

  return campaignId
    ? `${reportingSuiteRoute}/assistant/${campaignId}`
    : `${reportingSuiteRoute}/assistant`;
};

export const createRouteToReportingDashboardsForAllAdvertisers = (
  clientId?: number | string
) => {
  const reportingSuiteRoute =
    createRouteToReportingSuiteForAllAdvertisers(clientId);

  return `${reportingSuiteRoute}/dashboards`;
};

export const createRouteToReportingDashboards = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  const reportingSuiteRoute = createRouteToReportingSuiteByAdvertiserId(
    clientId,
    advertiserId
  );

  return `${reportingSuiteRoute}/dashboards`;
};

export const createRouteToReportingSuiteBuilder = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  const reportingSuiteRoute = createRouteToReportingSuiteByAdvertiserId(
    clientId,
    advertiserId
  );

  return `${reportingSuiteRoute}/builder`;
};

export const createRouteToReportingSuiteBuilderSchedules = (
  clientId?: number | string,
  advertiserId?: number | string
) => {
  const reportingSuiteBuilderRoute = createRouteToReportingSuiteBuilder(
    clientId,
    advertiserId
  );

  return `${reportingSuiteBuilderRoute}/schedules`;
};
